import React from "react";
import { Link } from "react-router-dom";
import { Phone, Zap, ShieldCheck, Building2, Calculator, ArrowRight, Wind, Snowflake, Fan, GitBranch, Wrench, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { COMPANY, GALLERY, HERO_IMAGE, SERVICES_ICONS, WHY_ICONS } from "../lib/constants";
import { useLang } from "../lib/i18n";
import Testimonials from "../components/Testimonials";

const ICON_MAP = { Zap, ShieldCheck, Building2, Calculator, Wind, Snowflake, Fan, GitBranch, Wrench };

export default function HomePage() {
  const { t } = useLang();

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border" data-testid="hero-section">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
        <div className="container-x relative grid lg:grid-cols-2 gap-12 items-center py-16 sm:py-24 lg:py-32">
          <div className="fade-up">
            <span className="overline">{t.hero.overline}</span>
            <h1 className="mt-5 font-display font-extrabold tracking-tight text-balance text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              {t.hero.headlineA} <span className="text-primary">{t.hero.headlineCool}</span>{t.hero.headlineComma}<br />
              {t.hero.headlineB}
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed">
              {t.hero.subheadline}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href={COMPANY.phoneHref} data-testid="hero-call-now-link">
                <Button
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2 rounded-full px-7 h-12 text-base font-semibold w-full sm:w-auto"
                  data-testid="hero-call-now-button"
                >
                  <Phone size={18} /> {t.common.call} {COMPANY.phoneDisplay}
                </Button>
              </a>
              <Link to="/contact" data-testid="hero-get-quote-link">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-7 h-12 text-base font-semibold border-2 border-foreground/20 hover:border-primary hover:text-primary w-full sm:w-auto"
                  data-testid="hero-get-quote-button"
                >
                  {t.common.getFreeQuote} <ArrowRight size={18} />
                </Button>
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-primary" /> {t.hero.trust1}</div>
              <div className="flex items-center gap-2"><Zap size={16} className="text-primary" /> {t.hero.trust2}</div>
              <div className="flex items-center gap-2"><MapPin size={16} className="text-primary" /> {t.hero.trust3}</div>
            </div>
          </div>

          <div className="relative fade-up delay-200">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-primary/10 border border-border">
              <img
                src={HERO_IMAGE}
                alt="Comfortable family enjoying cool air conditioning"
                className="w-full h-[420px] sm:h-[520px] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-primary/30 via-transparent to-transparent mix-blend-multiply" />
            </div>
            <div className="absolute -bottom-5 -left-5 sm:-bottom-7 sm:-left-7 bg-background border border-border rounded-xl shadow-xl p-4 sm:p-5 flex items-center gap-4 max-w-xs">
              <div className="h-12 w-12 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0">
                <Snowflake size={22} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{t.hero.badgeNow}</div>
                <div className="text-sm font-semibold leading-tight">{t.hero.badgeText}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="section-y" data-testid="why-choose-us">
        <div className="container-x">
          <div className="max-w-2xl">
            <span className="overline">{t.why.overline}</span>
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              {t.why.title}
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {t.why.items.map((item, i) => {
              const Icon = ICON_MAP[WHY_ICONS[i]] || Zap;
              return (
                <div
                  key={i}
                  data-testid={`why-card-${i}`}
                  className="group rounded-2xl border border-border bg-card p-6 sm:p-7 hover:-translate-y-1 hover:shadow-lg hover:border-primary/40 transition-all duration-300"
                >
                  <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary grid place-items-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <Icon size={22} strokeWidth={2.25} />
                  </div>
                  <h3 className="mt-5 font-display font-bold text-xl">{item.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="section-y bg-secondary/30 border-y border-border" data-testid="gallery-section">
        <div className="container-x">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
            <div className="max-w-xl">
              <span className="overline">{t.gallery.overline}</span>
              <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                {t.gallery.title}
              </h2>
            </div>
            <Link to="/services" className="text-sm font-semibold text-primary hover:underline inline-flex items-center gap-1" data-testid="gallery-services-link">
              {t.common.seeAllServices} <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {GALLERY.map((g, i) => (
              <figure
                key={g.url}
                className={`gallery-card group relative overflow-hidden rounded-2xl border border-border bg-background shadow-sm hover:shadow-xl transition-shadow ${
                  i === 0 ? "sm:col-span-2 sm:row-span-2 sm:aspect-[4/3.6]" : "aspect-[4/3]"
                }`}
                data-testid={`gallery-item-${i}`}
              >
                <img
                  src={g.url}
                  alt={t.gallery.captions[i] || g.alt}
                  loading="lazy"
                  className="gallery-img w-full h-full object-cover"
                />
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-5 text-white">
                  <div className="text-xs uppercase tracking-widest text-white/70">{t.gallery.jobPhoto}</div>
                  <div className="mt-1 font-display font-semibold text-base sm:text-lg">{t.gallery.captions[i]}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* SERVICES PREVIEW */}
      <section className="section-y" data-testid="services-preview">
        <div className="container-x">
          <div className="max-w-2xl">
            <span className="overline">{t.services.overline}</span>
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              {t.services.titleHome}
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {t.services.items.map((s, i) => {
              const Icon = ICON_MAP[SERVICES_ICONS[i]] || Snowflake;
              return (
                <Link
                  key={i}
                  to="/contact"
                  state={{ service: s.title, serviceIndex: i }}
                  data-testid={`home-service-card-${i}`}
                  className="group rounded-2xl border border-border bg-card p-6 sm:p-7 hover:-translate-y-1 hover:shadow-lg hover:border-primary/40 transition-all"
                >
                  <div className="h-11 w-11 rounded-lg bg-primary/10 text-primary grid place-items-center">
                    <Icon size={20} strokeWidth={2.25} />
                  </div>
                  <h3 className="mt-5 font-display font-semibold text-lg">{s.title}</h3>
                  <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary group-hover:gap-2 transition-all">
                    {t.common.requestServiceLower} <ArrowRight size={14} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="border-t border-border bg-primary text-primary-foreground" data-testid="cta-strip">
        <div className="container-x py-14 sm:py-20 grid lg:grid-cols-[1.5fr_1fr] gap-8 items-center">
          <div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              {t.ctaStrip.title}
            </h2>
            <p className="mt-3 text-primary-foreground/85 max-w-xl">
              {t.ctaStrip.subtitle}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href={COMPANY.phoneHref} className="flex-1" data-testid="cta-strip-call">
              <Button size="lg" className="w-full bg-white text-primary hover:bg-white/90 gap-2 rounded-full h-12 font-semibold">
                <Phone size={18} /> {COMPANY.phoneDisplay}
              </Button>
            </a>
            <Link to="/contact" className="flex-1" data-testid="cta-strip-quote">
              <Button size="lg" variant="outline" className="w-full rounded-full h-12 font-semibold border-2 border-white/40 bg-transparent text-white hover:bg-white hover:text-primary">
                {t.common.getFreeQuote}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
